import { supabase } from './supabaseClient';
import {
  decodeJwtParts,
  getHostnameFromUrl,
  getProjectRefFromHostname,
  safeTokenPreview,
} from './debug/jwtDebug';
import { isLocalhostDebug } from './debug/localDevLogger';
import { normalizeEnvKey } from './envKeyUtils';

const getFunctionsUrl = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (!supabaseUrl) {
    throw new Error('Missing REACT_APP_SUPABASE_URL');
  }
  return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/riddle`;
};

const getRiddleApiUrl = () => {
  // Allow explicit override (useful for local testing against Vercel dev).
  if (process.env.REACT_APP_RIDDLE_API_URL) {
    const overrideUrl = String(process.env.REACT_APP_RIDDLE_API_URL).trim();
    // Legacy safeguard: this app now serves riddles from Supabase Edge Functions.
    // If an old env var still points to a removed /api/* route, fall back safely.
    if (overrideUrl.startsWith('/api/')) {
      return getFunctionsUrl();
    }
    return overrideUrl;
  }

  // Always use Supabase Edge Function - it has much longer timeout limits (150s+)
  // compared to Vercel Serverless (10s on Hobby plan).
  return getFunctionsUrl();
};

const isRiddleDebugEnabled = () => {
  if (!isLocalhostDebug()) return false;
  return (
    process.env.REACT_APP_DEBUG_RIDDLE === 'true' ||
    (process.env.NODE_ENV !== 'production' &&
      process.env.REACT_APP_DEBUG_RIDDLE !== 'false')
  );
};

const withTimeout = async (promise, ms, label) => {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const error = new Error(`${label} timed out after ${ms}ms`);
      error.code = 'TIMEOUT';
      reject(error);
    }, ms);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }
};

let refreshInFlight = null;

const getValidAccessToken = async ({ forceRefresh = false } = {}) => {
  const { data: sessionData } = await supabase.auth.getSession();
  let session = sessionData?.session;
  if (!session?.access_token) {
    return null;
  }

  const expiresAtMs = session.expires_at ? session.expires_at * 1000 : null;
  const needsRefresh = expiresAtMs && Date.now() > expiresAtMs - 60_000;

  if (forceRefresh || needsRefresh) {
    if (!refreshInFlight) {
      refreshInFlight = (async () => {
        try {
          // Avoid hanging the UI forever if the network call stalls.
          return await withTimeout(
            supabase.auth.refreshSession(),
            7_000,
            'supabase.auth.refreshSession'
          );
        } finally {
          refreshInFlight = null;
        }
      })();
    }

    let refreshData;
    let refreshError;
    try {
      const res = await refreshInFlight;
      refreshData = res?.data;
      refreshError = res?.error;
    } catch (err) {
      refreshData = null;
      refreshError = err;
    }

    if (refreshError || !refreshData?.session?.access_token) {
      if (isRiddleDebugEnabled()) {
        console.warn('[riddleApi] refreshSession failed', {
          refreshError,
          hadSession: Boolean(session),
          expiresAt: session?.expires_at,
          forceRefresh,
        });
      }
      return null;
    }
    session = refreshData.session;
  }

  if (isRiddleDebugEnabled()) {
    const token = session.access_token;
    const decoded = decodeJwtParts(token);
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const functionsUrl = supabaseUrl
      ? `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/riddle`
      : null;

    const supabaseHost = supabaseUrl ? getHostnameFromUrl(supabaseUrl) : null;
    const functionsHost = functionsUrl ? getHostnameFromUrl(functionsUrl) : null;
    const issuerHost = decoded?.payload?.iss
      ? getHostnameFromUrl(decoded.payload.iss)
      : null;
    const projectMismatch =
      Boolean(supabaseHost && issuerHost) && supabaseHost !== issuerHost;

    console.log('[riddleApi] auth debug', {
      userId: session?.user?.id,
      expiresAt: session?.expires_at,
      needsRefresh,
      tokenPreview: safeTokenPreview(token),
      tokenPartsCount: decoded?.partsCount,
      jwtIss: decoded?.payload?.iss,
      jwtAud: decoded?.payload?.aud,
      jwtSub: decoded?.payload?.sub,
      jwtExp: decoded?.payload?.exp,
      supabaseHost,
      supabaseProjectRef: getProjectRefFromHostname(supabaseHost),
      functionsHost,
      issuerHost,
      issuerProjectRef: getProjectRefFromHostname(issuerHost),
      projectMismatch,
    });

    // Extra un-collapsed lines (easier to paste from console).
    console.log(
      `[riddleApi] jwt issuer host=${issuerHost} supabase host=${supabaseHost} mismatch=${projectMismatch}`
    );
    console.log(
      `[riddleApi] jwt iss=${decoded?.payload?.iss || '(missing)'} aud=${
        decoded?.payload?.aud || '(missing)'
      } exp=${decoded?.payload?.exp || '(missing)'}`
    );
  }

  return session.access_token;
};

const isInvalidJwtGatewayResponse = (response, responseText, dataJson) => {
  if (!response || response.status !== 401) return false;
  const msg =
    (typeof dataJson?.message === 'string' && dataJson.message) ||
    (typeof responseText === 'string' && responseText) ||
    '';
  return msg.includes('Invalid JWT');
};

const callRiddleOnce = async ({ word, difficulty, guessRow, accessToken, anonKey }) => {
  const payload = { word, difficulty, guessRow };
  const headers = {
    'Content-Type': 'application/json',
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  if (anonKey) {
    headers.apikey = anonKey;
  }

  if (isRiddleDebugEnabled()) {
    console.log('[riddleApi] calling edge function', {
      functionsUrl: getFunctionsUrl(),
      hasAnonKey: Boolean(anonKey),
      hasAuthorization: Boolean(headers.Authorization),
      wordLen: typeof word === 'string' ? word.length : null,
      difficulty,
    });
  }

  const response = await fetch(getFunctionsUrl(), {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  const responseText = await response.text();
  let dataJson = null;
  try {
    dataJson = responseText ? JSON.parse(responseText) : null;
  } catch {
    dataJson = null;
  }

  return { response, responseText, dataJson };
};

export const fetchRiddle = async (word, options = {}) => {
  const difficulty =
    typeof options?.difficulty === 'string' ? options.difficulty : 'hard';
  const guessRow =
    Number.isFinite(options?.guessRow) ? Math.floor(options.guessRow) : null;
  // Edge Functions gateway historically expects the `apikey` header to be the JWT-style anon key.
  // If you're using a new `sb_publishable_...` key for the JS client, provide the legacy JWT anon
  // key via REACT_APP_SUPABASE_JWT_ANON_KEY for Edge Function calls.
  const jwtAnonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_JWT_ANON_KEY);
  const anonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_ANON_KEY);

  // Prefer explicit legacy JWT anon key, but only if it actually decodes as a JWT.
  // If it's wrapped in quotes or malformed, sending it as `apikey` will cause gateway 401 Invalid JWT.
  const jwtAnonDecoded = decodeJwtParts(jwtAnonRaw);
  const jwtAnonIsUsableJwt =
    jwtAnonDecoded?.partsCount === 3 && !jwtAnonDecoded?.decodeError && jwtAnonDecoded?.payload;

  const anonKeyCandidate = jwtAnonIsUsableJwt ? jwtAnonRaw : anonRaw;
  const anonKeyDecoded = decodeJwtParts(anonKeyCandidate);
  const anonKeyLooksJwt =
    anonKeyDecoded?.partsCount === 3 && !anonKeyDecoded?.decodeError && anonKeyDecoded?.payload;
  const anonKey = anonKeyCandidate || null;

  if (!anonKey && isRiddleDebugEnabled()) {
    console.warn(
      '[riddleApi] No `apikey` key available. Set REACT_APP_SUPABASE_ANON_KEY (and optionally REACT_APP_SUPABASE_JWT_ANON_KEY).'
    );
  }
  let accessToken = await getValidAccessToken();

  if (accessToken) {
    // ok
  } else {
    const error = new Error('Missing auth session.');
    error.code = 'UNAUTHORIZED';
    error.status = 401;
    throw error;
  }

  const riddleUrl = getRiddleApiUrl();

  // Vercel Serverless path: same-origin API route.
  if (riddleUrl.startsWith('/api/')) {
    const response = await fetch(riddleUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ word, difficulty }),
    });

    const responseText = await response.text();
    let dataJson = null;
    try {
      dataJson = responseText ? JSON.parse(responseText) : null;
    } catch {
      dataJson = null;
    }

    if (!response.ok) {
      const error = new Error(dataJson?.error || responseText || 'Failed to fetch riddle.');
      error.code = dataJson?.code;
      error.status = response.status;
      throw error;
    }

    if (!dataJson?.riddle) {
      throw new Error('No riddle returned.');
    }

    return dataJson;
  }

  let { response, responseText, dataJson } = await callRiddleOnce({
    word,
    difficulty,
    guessRow,
    accessToken,
    anonKey,
  });

  // Recovery path: if Supabase gateway says "Invalid JWT", force-refresh and retry once.
  if (isInvalidJwtGatewayResponse(response, responseText, dataJson)) {
    if (isRiddleDebugEnabled()) {
      console.warn('[riddleApi] invalid jwt from gateway; forcing refresh + retry');
      console.log('[riddleApi] step 1/3: calling refreshSession...');
    }

    accessToken = await getValidAccessToken({ forceRefresh: true });

    if (isRiddleDebugEnabled()) {
      console.log('[riddleApi] step 2/3: refreshSession done, got token:', Boolean(accessToken));
    }

    if (!accessToken) {
      if (isRiddleDebugEnabled()) {
        console.warn('[riddleApi] refresh failed; NOT signing out (caller decides)');
      }
      const error = new Error(
        'Your session token was rejected by the Edge Functions gateway (Invalid JWT). Please use Account → Reset local session, then sign in again.'
      );
      error.code = 'INVALID_JWT';
      error.status = 401;
      throw error;
    }

    if (isRiddleDebugEnabled()) {
      console.log('[riddleApi] step 3/3: retrying edge function call...');
    }

    ({ response, responseText, dataJson } = await callRiddleOnce({
      word,
      difficulty,
      guessRow,
      accessToken,
      anonKey,
    }));

    if (isRiddleDebugEnabled()) {
      console.log('[riddleApi] retry complete, status:', response.status);
    }

  }

  if (!response.ok) {
    if (isRiddleDebugEnabled()) {
      console.warn('[riddleApi] edge function error', {
        status: response.status,
        responseText,
        dataJson,
      });
    }
    const error = new Error(dataJson?.error || responseText || 'Failed to fetch riddle.');
    error.code = dataJson?.code;
    error.status = response.status;
    throw error;
  }

  if (!dataJson?.riddle) {
    throw new Error('No riddle returned.');
  }

  return dataJson;
};
